/**
 * Schedule check: automatically open/close competitions and start/stop rounds
 * based on startTime/endTime. Run periodically (e.g. every 30s) from any open tab.
 */
import { db } from '../firebase/config';
import {
  collection,
  getDocs,
  getDoc,
  updateDoc,
  doc,
  writeBatch,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { timestampToDate } from './dateUtils';
import { pickAssignedChallengeIds, shuffleQuestionsEnabled } from './challengeOrder';

let isRunning = false;
const SCHEDULE_INTERVAL_MS = 30000; // 30 seconds

export async function runScheduleCheck() {
  if (isRunning) return;
  isRunning = true;
  try {
    const compsSnap = await getDocs(collection(db, 'competitions'));
    const now = new Date();

    for (const compDoc of compsSnap.docs) {
      const comp = { id: compDoc.id, ...compDoc.data() };
      const startDate = timestampToDate(comp.startTime);
      const endDate = timestampToDate(comp.endTime);

      // --- Competition: auto-open (has both times, not active, now in window, no other active) ---
      const anyOtherActive = compsSnap.docs.some(
        (d) => d.id !== comp.id && (d.data().isActive === true)
      );
      if (
        startDate &&
        endDate &&
        !comp.isActive &&
        !anyOtherActive &&
        now >= startDate &&
        now <= endDate
      ) {
        await updateDoc(doc(db, 'competitions', comp.id), {
          isActive: true,
          ...(comp.startTime ? {} : { startTime: serverTimestamp() })
        });
        continue;
      }

      // --- Competition: auto-close (active and past end) ---
      if (comp.isActive && endDate && now > endDate) {
        const rounds = [...(comp.rounds || [])];
        const activeRoundIndices = rounds
          .map((r, idx) => (r.isActive ? idx : null))
          .filter((i) => i !== null);
        const updatedRounds = rounds.map((r) =>
          r.isActive ? { ...r, isActive: false, hasStarted: true } : r
        );
        await updateDoc(doc(db, 'competitions', comp.id), {
          isActive: false,
          rounds: updatedRounds,
          ...(comp.endTime ? {} : { endTime: serverTimestamp() })
        });
        if (activeRoundIndices.length > 0) {
          const usersSnap = await getDocs(collection(db, 'competitions', comp.id, 'users'));
          const batch = writeBatch(db);
          const endTime = new Date();
          usersSnap.docs.forEach((userDoc) => {
            const user = userDoc.data();
            if (user.frozen) return;
            const roundData = user.roundData || {};
            const updatedRoundData = { ...roundData };
            activeRoundIndices.forEach((roundIndex) => {
              const current = roundData[roundIndex] || {};
              updatedRoundData[roundIndex] = { ...current, endTime };
            });
            batch.update(doc(db, 'competitions', comp.id, 'users', userDoc.id), {
              roundData: updatedRoundData
            });
          });
          await batch.commit();
        }
        continue;
      }

      // --- Rounds: only for active competition ---
      if (!comp.isActive || !comp.rounds?.length) continue;
      let rounds = [...comp.rounds];

      // --- Auto-stop: round is active and past its endTime ---
      for (let i = 0; i < rounds.length; i++) {
        const r = rounds[i];
        const rEnd = timestampToDate(r.endTime);
        if (r.isActive && rEnd && now > rEnd) {
          rounds[i] = { ...r, isActive: false, hasStarted: true, endTime: r.endTime || serverTimestamp() };
          await updateDoc(doc(db, 'competitions', comp.id), { rounds });
          const usersSnap = await getDocs(collection(db, 'competitions', comp.id, 'users'));
          const batch = writeBatch(db);
          const endTime = new Date();
          usersSnap.docs.forEach((userDoc) => {
            const user = userDoc.data();
            if (user.frozen) return;
            const roundData = user.roundData || {};
            const currentRoundData = roundData[i] || {};
            batch.update(doc(db, 'competitions', comp.id, 'users', userDoc.id), {
              roundData: {
                ...roundData,
                [i]: { ...currentRoundData, endTime }
              }
            });
          });
          await batch.commit();
          break; // one round stop per run
        }
      }

      // Re-fetch after possible stop so we have latest rounds
      const compRefreshed = (await getDoc(doc(db, 'competitions', comp.id))).data();
      rounds = compRefreshed?.rounds || rounds;
      const hasActiveRound = rounds.some((r) => r.isActive);
      if (hasActiveRound) continue;

      // --- Auto-start: find first round that should be running ---
      for (let i = 0; i < rounds.length; i++) {
        const r = rounds[i];
        const rStart = timestampToDate(r.startTime);
        const rEnd = timestampToDate(r.endTime);
        if (!rStart || !rEnd) continue;
        if (now < rStart || now > rEnd) continue;
        const prev = i > 0 ? rounds[i - 1] : null;
        if (prev && (!prev.hasStarted || prev.isActive)) continue;
        if (prev) {
          const prevEnd = timestampToDate(prev.endTime);
          if (prevEnd && prevEnd > rStart) continue;
        }

        // Re-fetch to avoid double-start from multiple tabs
        const fresh = (await getDoc(doc(db, 'competitions', comp.id))).data();
        const freshRounds = fresh?.rounds || rounds;
        if (freshRounds[i]?.isActive) continue;

        const newRounds = [...freshRounds];
        newRounds[i] = { ...freshRounds[i], isActive: true, hasStarted: true, startTime: r.startTime || serverTimestamp() };
        await updateDoc(doc(db, 'competitions', comp.id), { rounds: newRounds });

        const challengesRef = collection(db, 'competitions', comp.id, 'challenges');
        const q = query(challengesRef, where('category', 'in', r.categories || []));
        const chSnap = await getDocs(q);
        const allChallenges = chSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const numCh = r.type === 'fixed' ? (r.numberOfChallenges || 0) : allChallenges.length;

        const usersSnap = await getDocs(collection(db, 'competitions', comp.id, 'users'));
        const batch = writeBatch(db);
        usersSnap.docs.forEach((userDoc) => {
          const user = userDoc.data();
          if (user.frozen) return;
          const roundData = user.roundData || {};
          const currentRoundData = roundData[i] || {};
          let assignedIds = [];
          if (allChallenges.length > 0 && numCh > 0) {
            assignedIds = pickAssignedChallengeIds(
              allChallenges,
              numCh,
              shuffleQuestionsEnabled(r)
            );
          }
          const challengeStates = {};
          assignedIds.forEach((id) => {
            challengeStates[id] = 'notsolved';
          });
          batch.update(doc(db, 'competitions', comp.id, 'users', userDoc.id), {
            roundData: {
              ...roundData,
              [i]: {
                ...currentRoundData,
                startTime: currentRoundData.startTime || null,
                score: currentRoundData.score || 0,
                correctAnswers: currentRoundData.correctAnswers || 0,
                assignedChallenges: assignedIds,
                challengeStates,
                isDone: currentRoundData.isDone === true
              }
            }
          });
        });
        await batch.commit();
        break; // one start per run
      }
    }
  } catch (err) {
    console.error('Schedule check error:', err);
  } finally {
    isRunning = false;
  }
}

export function getScheduleIntervalMs() {
  return SCHEDULE_INTERVAL_MS;
}
